import type { Translations } from './de'

/**
 * English translations. Secondary locale; mirrors the de.ts shape exactly
 * so the type system catches any drift. The type is derived from de.ts.
 */
export const en: Translations = {
  common: {
    today: 'Today',
    back: 'Back',
    close: 'Close',
    open: 'Open',
    pause: 'Pause',
    resume: 'Resume',
    start: 'Start',
    complete: 'Complete',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    signOut: 'Sign out',
    signOutFailed: 'Sign-out failed — you are still signed in. Check your connection and try again.',
    signOutEverywhere: 'Sign out everywhere else',
    signOutEverywhereHint: "Ends every other session but this one — use it if you have lost a device or signed in on a shared machine.",
    signOutEverywhereDone: 'Signed out of {count} other device(s).',
    signOutEverywhereNone: 'No other devices were signed in.',
    signOutEverywhereFailed: 'Could not sign out the other devices. Try again.',
    profile: 'My profile',
    switchToDark: 'Switch to dark',
    switchToLight: 'Switch to light',
    notifications: 'Notifications',
    menu: 'Menu',
  },
  confirm: {
    addPartTitle: 'Add this part?',
    addPartCta: 'Add',
    requestPartTitle: 'Request this part?',
    requestPartCta: 'Send request',
    removePartTitle: 'Remove this part?',
    removePartCta: 'Remove',
    // Two different commitments — see de.ts. TODO(copy).
    claimServiceTitle: 'Take this service?',
    claimServiceCta: 'Take',
    claimTitle: 'Take this step?',
    claimCta: 'Take',
    // The whole job — see de.ts. TODO(copy).
    claimJobTitle: 'Take this job?',
    claimJobCta: 'Take',
  },
  nav: {
    today: 'Today',
    inventory: 'Inventory',
    services: 'Services',
  },
  // The topbar's filter bar — what replaced the nav pills. `board` is the
  // whole screen as it was; the rest are slices of it.
  filters: {
    board: 'My board',
    undispatched: 'Undispatched',
    progress: 'In progress',
    upcoming: 'Upcoming',
    done: 'Done',
    label: 'Filter jobs',
    emptyUndispatched: 'Nothing waiting to be taken.',
    emptyProgress: 'Nothing running right now.',
    emptyUpcoming: 'Nothing booked for later.',
    emptyDone: 'Nothing finished yet.',
    // Says the cap out loud rather than quietly truncating — /active has
    // no paging, so this is the most recent 20, not everything.
    doneCapped: 'Showing your 20 most recent.',
    scheduledFor: 'Booked for',
    finished: 'Finished',
  },
  // DRAFT — see the note on de.ts.
  errors: {
    offline: "No connection — your change wasn't saved.",
    server: 'Server problem — please try again.',
    rejected: "That action wasn't possible.",
    retry: 'Try again',
    dismiss: 'Dismiss',
  },
  home: {
    regionLabel: "Today's tasks",
    loading: 'Loading…',
    errorPanel: "Couldn't load the task list.",
    errorWorkOrder: 'Could not load work order.',
    openWO: 'Open WO',
    yourWork: 'Your jobs',
    openJobs: 'Open jobs',
    otherWork: 'Everything else',
    doneToday: (n: number) => `Done today (${n})`,
    emptyTitle: 'Quiet for now',
    emptySub: 'No open jobs. New jobs appear here as soon as Management creates them.',
    bottomBarLabelActive: 'Active job',
    bottomBarLabelIdle: 'Up next',
    bottomBarLabelResume: 'Resume work',
  },
  tasks: {
    // Home (do-list) — grouped by work order, vehicle-first.
    claimError: 'Could not claim the task.',
    claimJob: 'Take job',
    claimJobError: 'Could not take the job.',
    // The per-service claim, reintroduced (2026-09-22): once the tech holds
    // a stake elsewhere on the job, a loose row offers this instead of the
    // whole-job claim above. TODO(copy).
    takeService: 'Take service',
    // A job dispatched with zero services (2026-09-22) — the office adds
    // them; no "Take" control, no service rows. TODO(copy).
    noServiceYet: 'No service yet — the office adds it.',
    statusOpen: 'Open',
    statusPending: 'Awaiting approval',
    statusRejected: 'Rejected',
    statusDone: 'Done',
    // Task-first Today card (concept 1b).
    you: 'You',
    running: 'Running',
    doneCount: (n: number) => `${n} done`,
    moreOpen: (n: number) => `+${n} more open`,
    est: (duration: string) => `est ${duration}`,
    // The time budget on a job — agreed vs spent vs what is left. `worked`
    // counts EVERY mechanic, because that is what the estimate was given
    // for; `own` is the reader's own share and never drives the subtraction.
    budget: {
      agreed: 'est.',
      worked: 'worked',
      left: 'left',
      over: 'over',
      own: 'yours',
      running: 'Timer running',
      paused: 'Timer paused',
      idle: 'Not started',
    },
    // Services + task states (WO → service → task).
    service: 'Service',
    yourTotal: 'your total',
    onIt: 'is on it',
    yoursLogged: (time: string) => `yours · ${time} logged`,
    pause: 'Pause',
    resume: 'Resume',
    awaitingCustomer: 'waiting on customer OK',
    approval: 'Approval',
    part: 'Part',
    searchParts: 'Search parts…',
    start: 'Start',
    claimedNotStarted: 'claimed, not started',
    // WO detail — task cards.
    sectionTitle: 'Tasks',
    empty: 'No tasks on this work order.',
    loadError: 'Could not load the tasks.',
    markDone: 'Done',
    markDoneError: 'Could not complete the task.',
    estimate: (price: string, duration: string) => `estimated ${price} · ${duration}`,
    // Propose-task modal ("Additional work").
    propose: 'Additional work',
    proposeHint: 'Reported to Management for customer approval. Workable once approved.',
    proposeTitleLabel: 'Title',
    proposeTitlePlaceholder: 'e.g. Replace front brake pads',
    proposeNoteLabel: 'Note (optional)',
    proposeServicesLabel: 'Catalog services (optional)',
    proposeSubmit: 'Send request',
    proposeCancel: 'Cancel',
    proposeError: 'Could not send the request.',
  },
  login: {
    pageTitle: 'Sign in',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    forgotPassword: 'Forgot password?',
    submit: 'Sign in',
    submitting: 'Signing in…',
    invalidCredentials: 'Invalid email or password.',
    genericError: 'Sign in failed. Please try again.',
    invalidEmailFormat: 'Invalid email address',
    bannerInvalidCredentials: 'Invalid credentials',
    bannerNetworkError: 'Connection failed',
    continueWithGoogle: 'Continue with Google',
    oauthDivider: 'or',
    bannerOauthCancelled: 'Google sign-in cancelled',
    bannerOauthFailed: 'Google sign-in failed',
    // TODO(copy)
    bannerOauthUnknownAccount:
      'This Google account is not set up for Vollgas. Please contact an administrator.',
  },
  healthGate: {
    serverUnavailable: 'Server unavailable',
    serverUnavailableHint: "We can't reach the workshop server right now. Please try again.",
    retry: 'Retry',
    slow: [
      'Getting the workshop ready…',
      'Checking parts inventory…',
      'Polishing the wrenches…',
      'Topping up the coffee…',
      'Lining up appointments…',
    ] as ReadonlyArray<string>,
  },
  workOrder: {
    regionLabel: 'Work order detail',
    mileageAtStart: 'Mileage at start',
    mileageAtComplete: 'Mileage at completion',
    completed: 'Completed',
    timeLogged: 'Time logged',
    timerRunning: 'Running',
    timerPaused: 'Paused',
    timerStarted: (time: string, day: string) => `Started ${time} · ${day}`,
    manualTimeEntry: 'Forgot to start the timer? Log time manually',
    addNote: 'Add a note…',
    // TODO(copy): placeholder wording for the chat thread (2026-09-22).
    sendNote: 'Send',
    noNotes: 'No notes yet.',
    noteError: 'Could not save the note.',
    notePreview: 'Preview',
    quickNoteFoilingDone: 'Foiling done',
    quickNoteBrakesOk: 'Brakes OK',
    parts: 'Parts',
    partRequestPending: 'requested · waiting on management',
    partRequestAccepted: 'approved · manager adds the part',
    partRequestDeclined: 'declined',
    partRequestWithdrawn: 'withdrawn',
    withdrawPartRequest: 'Withdraw',
    partsTotal: 'Parts total',
    completeWork: 'Complete work',
    // TODO(copy): placeholder wording — needs Roham's verbatim text.
    handedBack: 'Handed back — the advisor is reviewing this job. Nothing more to do here.',
    reopenWork: 'Reopen work order',
    // WO detail (concept 1a) — accordion.
    done: 'done',
    show: 'show',
    km: 'km',
    sku: 'SKU',
    logTimeManually: 'log time manually',
    finishTask: 'Finish service',
    addPart: 'Part',
    claimToStart: 'Take the job to start working.',
    partsCount: (n: number) => `${n} ${n === 1 ? 'part' : 'parts'}`,
    allTasksDone: (time: string) => `All services done — ${time} total`,
    completeHint: 'Complete work hands the job back to the advisor for review.',
    jobNotes: 'Job notes',
    // Confirmed by the user 2026-09-22.
    newNoteBadge: 'New note on this job',
    assignedTo: 'Assigned to',
    unassigned: 'Not assigned to anyone yet',
    noDescription: 'No description from the advisor.',
    description: 'Description',
    // Generic fallback ONLY. It used to be shown for every failure that
    // was not "tasks still open", so a permissions error told the shop to
    // go and log time — advice that could never fix it. Specific causes
    // get their own line below.
    completeError: 'Could not finish the work order. Please try again.',
    // TODO(copy): placeholder wording — needs Roham's verbatim text.
    completeErrEmpty: 'Nothing is recorded on this job yet — log some time, add a part, or add the service that was performed.',
    completeErrForbidden: 'You are not on this job, so you cannot hand it back. Ask the manager to assign it to you.',
    // TODO(copy): pending Figma wording.
    proposalPending: 'A proposed job is still waiting on the customer. It does not hold the car — the advisor decides on it.',
    completeBlockedByTasks: 'This job picked up new work while you were here — finish the open services, then hand it back.',
    additionalService: 'Additional service',
    removePart: 'Remove',
    saved: 'Saved',
    saveFailed: 'Save failed',
  },
  inventory: {
    title: 'Inventory',
    subtitle: 'Workshop stock levels.',
    search: 'Search parts…',
    onHand: 'On hand',
    reorder: 'Reorder at',
    lowStock: 'Low',
    empty: 'No parts found.',
    loadError: 'Could not load inventory.',
    unitPrice: 'Unit price',
  },
  reorder: {
    button: 'Reorder material',
    title: 'Reorder material',
    hint: 'Tell management which materials are used up and need reordering.',
    searchPlaceholder: 'Search or type a new material…',
    addNew: (name: string) => `Add “${name}” as a new material`,
    newBadge: 'New',
    listLabel: 'Materials to reorder',
    empty: 'No materials added yet.',
    quantity: 'Qty',
    notePlaceholder: 'Note (optional) — e.g. brand, size',
    remove: 'Remove',
    submit: 'Send to management',
    submitting: 'Sending…',
    cancel: 'Cancel',
    success: 'Reorder request sent to management.',
    error: 'Could not send. Please try again.',
  },
  manualLabour: {
    title: 'Log time manually',
    hint: 'Forgot to start the timer? Enter the span you worked — it counts toward this task, same as the live timer.',
    start: 'Start',
    end: 'End',
    addBreak: 'Add a break',
    breakStart: 'Break start',
    breakEnd: 'Break end',
    submit: 'Log time',
    errorSpan: 'Enter a start and end time.',
    errorOrder: 'End must be after start.',
    errorBreak: 'Check the break times.',
    errorSubmit: 'Could not log the time. Please try again.',
  },
  proposeService: {
    title: 'Propose additional work',
    hint: 'Found more that needs doing? This creates a task the manager clears with the customer before it can be worked.',
    workTitle: 'What needs doing',
    workTitlePlaceholder: 'e.g. Replace front brake discs',
    service: 'Service (optional)',
    noService: 'No catalog service',
    note: 'Note (optional)',
    notePlaceholder: 'Anything the advisor should tell the customer',
    submit: 'Propose work',
    errorTitle: 'Describe the work first.',
    errorSubmit: 'Could not propose the work. Please try again.',
  },
  walkin: {
    title: 'Walk-in',
    subtitle: 'Create a work order without an appointment.',
    stepCustomer: 'Customer',
    stepVehicle: 'Vehicle',
    stepService: 'Service',
    searchCustomer: 'Search customer (name, phone)…',
    noCustomers: 'No customers found.',
    newCustomerUnavailable: 'Creating a new walk-in customer is not available yet — please search for an existing one or have the office create it.',
    selectVehicle: 'Select vehicle',
    noVehicles: 'No vehicles for this customer.',
    addVehicle: 'Add vehicle',
    plate: 'Plate',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    saveVehicle: 'Save vehicle',
    mileageAtIntake: 'Mileage at intake (optional)',
    startWork: 'Start work',
    back: 'Back',
    next: 'Next',
    createError: 'Could not create the work order.',
    mileageInvalid: 'Enter a valid mileage (0 or higher).',
    yearInvalid: 'Enter a valid year.',
  },
  addPart: {
    title: 'Add part',
    search: 'Search the catalogue…',
    empty: 'No parts found.',
    quantity: 'Quantity',
    add: 'Add',
    cancel: 'Cancel',
    addError: 'Could not add the part.',
    addNew: 'Add new part',
    taskTarget: 'Task',
  },
  otherLine: {
    title: 'Other line',
    hint: 'For parts not in the catalogue. Flagged to Management for review.',
    description: 'Description',
    descriptionPlaceholder: 'e.g. Special gasket',
    unitPrice: 'Unit price (€)',
    quantity: 'Quantity',
    vatRate: 'VAT rate (%)',
    add: 'Add line',
    cancel: 'Cancel',
    addError: 'Could not add the line.',
  },
  complete: {
    title: 'Complete work',
    review: 'Review notes, time, and parts. Finalise completion?',
    timeLogged: 'Time logged',
    partsTotal: 'Parts total',
    notesCount: (n: number) => `${n} ${n === 1 ? 'note' : 'notes'}`,
    mileagePrompt: 'Mileage now? (leave blank to skip)',
    confirm: 'Confirm completion',
    cancel: 'Cancel',
    completeError: 'Completion failed.',
    autoInvoice: 'An invoice draft will be auto-generated for Management.',
  },
  manualTime: {
    title: 'Log time manually',
    date: 'Date',
    workPeriod: 'Work period',
    pauses: 'Breaks',
    addPause: 'Add break',
    noPauses: 'No breaks',
    removePause: 'Remove break',
    worked: 'Logged',
    breakTotal: 'Break',
    add: 'Add time',
    cancel: 'Cancel',
    addError: 'Could not log the time.',
    invalidRange: 'End must be after start.',
  },
  reopen: {
    title: 'Reopen work order',
    hint: 'Allowed within 1 hour of completion. The reason is logged.',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'Why are you reopening this work order?',
    submit: 'Reopen',
    cancel: 'Cancel',
    error: 'Could not reopen the work order.',
  },
  partRequest: {
    title: 'Request a part',
    hint: 'Parts are approved by Management — the request will be reviewed.',
    partLabel: 'Part',
    partPlaceholder: 'e.g. Front brake pads',
    quantity: 'Quantity',
    note: 'Note (optional)',
    submit: 'Send request',
    submitError: 'Could not send the request.',
    sent: 'Request sent — Management will review it.',
    another: 'New request',
  },
  services: {
    title: 'Services',
    subtitle: 'Workshop service categories.',
    search: 'Search services…',
    empty: 'No services found.',
    loadError: 'Could not load the services.',
  },
  notifications: {
    title: 'Notifications',
    empty: 'No new notifications.',
    loadError: 'Could not load notifications.',
    dismiss: 'Dismiss',
    // Only clears notifications assigned to this technician — shop-wide
    // ones stay, because clearing them would hide them from everyone. So
    // "all" is not literally true when the list also holds shop-wide
    // rows: those have no X either, and stay put. Chosen anyway over a
    // bare "Dismiss", which was indistinguishable from the per-row
    // control by accessible name.
    dismissAll: 'Dismiss all',
  },
  profile: {
    title: 'My profile',
    role: 'Role',
    email: 'Email',
    language: 'Language',
    password: 'Password',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Repeat new password',
    show: 'Show',
    hide: 'Hide',
    errLength: 'Password must be at least 8 characters.',
    errMismatch: 'The two new passwords do not match.',
    errCurrentWrong: 'Current password is incorrect.',
    // AuthService.changePassword revokes every session but the caller's
    // (revokeAllForUserExcept). On a shop floor that is several tablets,
    // so it is said plainly rather than discovered at the next one.
    sessionHint: 'You stay signed in here. Other tablets are signed out.',
    saving: 'Saving…',
    errGeneric: 'Could not change the password. Try again.',
    success: 'Password changed.',
  },
}
